/**
 * Business Brain - Phase BB1 (read-only cockpit).
 *
 * Unified view of the four configuration surfaces:
 *   - Business identity / ICP / IAP / geography  (business_settings)
 *   - AI persona + behavior                      (business_settings + ai_behavior_settings)
 *   - Knowledge docs grouped by UI group          (knowledge_documents)
 *   - Auto sources                                (knowledge_sources)
 *   - Portfolio health                            (business/portfolio-summary)
 *
 * Read-only by design - every section ends with an "Edit in /X →" link to
 * the existing detailed page. No edit forms here.
 *
 * Backend contract: uses ONLY existing endpoints. If any of them fails or
 * doesn't exist, the corresponding section fails soft with "Not available
 * yet" and the rest of the page keeps working.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CheckSquare, Building2, Target, Package, Bot,
  ScrollText, MessageSquare, Shield, MapPin, Globe, Sparkles, Activity,
  ChevronDown, ExternalLink, Filter,
  CheckCircle2, Circle,
  HelpCircle, BarChart3, AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { parseBackendDate } from "@/lib/format";

// ===========================================================================
// Types
// ===========================================================================

interface BusinessSettings {
  business_name?: string;
  app_brand_name?: string | null;
  tagline?: string | null;
  description?: string | null;
  offering_description?: string | null;
  audience_description?: string | null;
  icp_description?: string | null;
  icp_painpoints?: string | null;
  icp_what_they_seek?: string | null;
  iap_avatar_name?: string | null;
  iap_avatar_story?: string | null;
  iap_avatar_vocabulary?: string | null;
  iap_avatar_objections?: string | null;
  cities_covered?: string[] | string | null;
  areas_not_covered?: string | null;
  bot_tone_of_voice?: string | null;
  bot_response_length?: string | null;
  bot_emoji_policy?: string | null;
  bot_addressing?: string | null;
  bot_special_do?: string | null;
  bot_special_dont?: string | null;
  bot_always_mention?: string | null;
  bot_always_avoid?: string | null;
  brand_logo_url?: string | null;
  embedding_model?: string | null;
  business_language?: string | null;
  default_visit_agent?: string | null;
}

interface KnowledgeDoc {
  id: string;
  title: string;
  category: string;
  priority: number;
  is_active: number;
  body_preview: string;
  updated_at: string;
}

interface KnowledgeSource {
  id: string;
  source_type: string;
  url: string;
  label: string | null;
  status: string;
  doc_count: number;
  last_scraped_at: string | null;
  last_error: string | null;
}

interface PortfolioSummary {
  counts: { total: number; active: number; available_now: number };
  by_stay: { long_term: number; medium_term: number; short_term: number; unspecified: number };
  by_type: { villa: number; apartment: number; house: number; room: number; land: number };
  areas: string[];
}

// What the list endpoint returns (subset of the full property row). Some
// per-villa rule fields like `pets_policy`, `smoking_policy`, etc. live on
// the row but the LIST query doesn't return them - see the source-of-truth
// notice for that.
interface PropertyListItem {
  id: string;
  name: string;
  area: string;
  bedrooms: number | null;
  bathrooms: number | null;
  monthly_price_idr: number | null;
  deposit_idr: number | null;
  status: string;
  available_from: string | null;
  min_stay_months: number | null;
  short_description: string | null;
  is_active: number;
}

interface AiBehavior {
  model?: string | null;
  reply_temperature?: number | null;
  extraction_temperature?: number | null;
  max_tokens?: number | null;
  hot_lead_score?: number | null;
  escalation_score?: number | null;
  vertical_addendum?: string | null;
}

interface SecretsStatus {
  database_url: boolean;
  admin_token: boolean;
  openrouter_api_key: boolean;
  waha_api_key: boolean;
  waha_base_url: boolean;
  waha_default_session: boolean;
  resend_api_key: boolean;
  telegram_bot_token: boolean;
  meta_app_secret: boolean;
  meta_verify_token: boolean;
  meta_page_access_token: boolean;
}

// Map current Knowledge categories into UI groups.
const KB_GROUP: Record<string, string> = {
  payment_terms:           "policies",
  viewing_rules:           "policies",
  contract_rules:          "policies",
  sales_scripts:           "sales",
  qualification_scripts:   "sales",
  objection_handling:      "objections",
  area_knowledge:          "area",
  property_matching_rules: "matching",
  company_rules:           "offer",
  faq:                     "faq",
};

// ===========================================================================
// Page
// ===========================================================================

export function BusinessBrainPage() {
  // Parallel queries - each is fail-soft (retry: 1, then isError surfaced).
  const bizQ = useQuery({
    queryKey: ["bb", "business"],
    queryFn: () => api.get<BusinessSettings>("/api/settings/business"),
    retry: 1,
  });
  const portfolioQ = useQuery({
    queryKey: ["bb", "portfolio"],
    queryFn: () => api.get<PortfolioSummary>("/api/business/portfolio-summary"),
    retry: 1,
  });
  const propsQ = useQuery({
    queryKey: ["bb", "properties"],
    queryFn: () => api.get<PropertyListItem[]>("/api/properties?limit=500"),
    retry: 1,
  });
  const knowledgeQ = useQuery({
    queryKey: ["bb", "knowledge"],
    queryFn: () => api.get<KnowledgeDoc[]>("/api/knowledge?limit=500&is_active=true"),
    retry: 1,
  });
  const sourcesQ = useQuery({
    queryKey: ["bb", "sources"],
    queryFn: () => api.get<KnowledgeSource[]>("/api/knowledge/sources"),
    retry: 1,
  });
  const aiBehaviorQ = useQuery({
    queryKey: ["bb", "ai-behavior"],
    queryFn: () => api.get<AiBehavior>("/api/settings/ai-behavior"),
    retry: 1,
  });
  const secretsQ = useQuery({
    queryKey: ["bb", "secrets"],
    queryFn: () => api.get<SecretsStatus>("/api/system/secrets-status"),
    retry: 1,
  });

  const biz = bizQ.data;
  const knowledge = knowledgeQ.data ?? [];
  const sources = sourcesQ.data ?? [];
  const portfolio = portfolioQ.data;
  const properties = propsQ.data ?? [];
  const aiBehavior = aiBehaviorQ.data;
  const secrets = secretsQ.data;

  const docsByGroup = groupKnowledge(knowledge);
  const checklist = computeChecklist({ biz, portfolio, knowledge, secrets });
  const coverage = computeCoverage(knowledge);
  const propsHealth = computePropertiesHealth(properties);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Business Brain</h1>
        <p className="text-sm text-slate-500">
          A unified read-only view of the business context, AI configuration, Knowledge Base, and Properties health.
          To edit, click the link at the bottom of each section.
        </p>
      </header>

      {/* 1. Setup Checklist */}
      <Section
        icon={<CheckSquare className="h-4 w-4" />}
        title="Setup Checklist"
        subtitle={`${checklist.done}/${checklist.total} done`}
        defaultOpen
      >
        <ul className="space-y-1.5">
          {checklist.items.map((it) => (
            <li key={it.label} className="flex items-start gap-2 text-sm flex-wrap">
              {it.done ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />
              ) : (
                <Circle className="h-4 w-4 mt-0.5 text-slate-300 shrink-0" />
              )}
              <span className={it.done ? "text-slate-700" : "text-slate-500"}>
                {it.label}
              </span>
              {it.hint && (
                <span className="text-xs text-slate-400 ml-2">- {it.hint}</span>
              )}
              {!it.done && it.category && (
                <>
                  <Badge variant="danger">Missing</Badge>
                  <Link
                    to={`/knowledge?category=${it.category}`}
                    className="text-[11px] text-slate-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
                  >
                    Add in Knowledge
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </>
              )}
            </li>
          ))}
        </ul>
      </Section>

      {/* 2. Business Identity */}
      <Section
        icon={<Building2 className="h-4 w-4" />}
        title="Business Identity"
        editHref="/business"
        editLabel="Edit Business"
        loading={bizQ.isLoading}
        error={bizQ.isError ? "Could not load business settings." : null}
      >
        {biz && (
          <div className="space-y-2 text-sm">
            <KV label="Name" value={biz.business_name} />
            <KV label="Tagline" value={biz.tagline} />
            <KV label="Description" value={biz.description} multiline />
            <KV label="Default language" value={biz.business_language} />
            <KV label="Default visit agent" value={biz.default_visit_agent} />
            {biz.brand_logo_url && (
              <div className="flex items-center gap-2 pt-2 text-xs text-slate-500">
                <img
                  src={biz.brand_logo_url}
                  alt="logo"
                  className="h-8 w-8 rounded object-contain border border-slate-200"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
                Logo set
              </div>
            )}
          </div>
        )}
      </Section>

      {/* 3. Customer Profile (ICP + IAP) */}
      <Section
        icon={<Target className="h-4 w-4" />}
        title="Customer Profile"
        subtitle="ICP + persona"
        editHref="/business"
        editLabel="Edit Business"
        loading={bizQ.isLoading}
        error={bizQ.isError ? "Could not load business settings." : null}
      >
        {biz && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Ideal Customer Profile (ICP)
              </div>
              <KV label="Description" value={biz.icp_description} multiline />
              <KV label="Pain points" value={biz.icp_painpoints} multiline />
              <KV label="What they seek" value={biz.icp_what_they_seek} multiline />
            </div>
            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Ideal Avatar Persona (IAP)
              </div>
              <KV label="Name" value={biz.iap_avatar_name} />
              <KV label="Story" value={biz.iap_avatar_story} multiline />
              <KV label="Vocabulary" value={biz.iap_avatar_vocabulary} multiline />
              <KV label="Typical objections" value={biz.iap_avatar_objections} multiline />
            </div>
            {biz.audience_description && (
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Audience (free-form)
                </div>
                <KV label="Description" value={biz.audience_description} multiline />
              </div>
            )}
          </div>
        )}
      </Section>

      {/* 4. Offer & Positioning */}
      <Section
        icon={<Package className="h-4 w-4" />}
        title="Offer & Positioning"
        subtitle="What you offer + company rules"
        editHref="/business"
        editLabel="Edit Business"
        loading={bizQ.isLoading || knowledgeQ.isLoading}
        error={bizQ.isError ? "Could not load business settings." : null}
      >
        <div className="space-y-3 text-sm">
          {biz && <KV label="Offering description" value={biz.offering_description} multiline />}
          <KnowledgeGroup
            label="Company rules (from Knowledge)"
            docs={docsByGroup.offer}
            categories={["company_rules"]}
            empty="No company rules yet."
            emptyHint="Global rules the AI must always respect (no smoking, no pets, no parties, identity check, etc.)."
            exampleTitle="House rules - no parties, no smoking indoors"
          />
          <DefaultsNotice />
        </div>
      </Section>

      {/* 5. Voice & Bot Behavior */}
      <Section
        icon={<Bot className="h-4 w-4" />}
        title="Voice & Bot Behavior"
        subtitle="How the AI speaks"
        editHref="/ai-bot"
        editLabel="Edit AI Bot"
        loading={bizQ.isLoading || aiBehaviorQ.isLoading}
        error={bizQ.isError ? "Could not load business settings." : null}
      >
        {biz && (
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Persona</div>
              <KV label="Tone of voice" value={biz.bot_tone_of_voice} />
              <KV label="Response length" value={biz.bot_response_length} />
              <KV label="Emoji policy" value={biz.bot_emoji_policy} />
              <KV label="Addressing" value={biz.bot_addressing} />
              <KV label="Always mention" value={biz.bot_always_mention} />
              <KV label="Always avoid" value={biz.bot_always_avoid} />
              <KV label="Things to DO" value={biz.bot_special_do} multiline />
              <KV label="Things to NEVER DO" value={biz.bot_special_dont} multiline />
            </div>
            {aiBehavior && !aiBehaviorQ.isError && (
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Behavior settings</div>
                <KV label="Default model" value={aiBehavior.model} />
                <KV label="Reply temperature" value={fmtNum(aiBehavior.reply_temperature)} />
                <KV label="Extraction temperature" value={fmtNum(aiBehavior.extraction_temperature)} />
                <KV label="Max tokens" value={fmtNum(aiBehavior.max_tokens)} />
                <KV label="Hot lead score threshold" value={fmtNum(aiBehavior.hot_lead_score)} />
                <KV label="Escalation score threshold" value={fmtNum(aiBehavior.escalation_score)} />
              </div>
            )}
            {aiBehaviorQ.isError && (
              <NotAvailable label="AI behavior" />
            )}
          </div>
        )}
      </Section>

      {/* 6. Property Matching Rules */}
      <Section
        icon={<Filter className="h-4 w-4" />}
        title="Property Matching Rules"
        subtitle="How the AI suggests alternatives"
        editHref="/knowledge"
        editLabel="Edit Knowledge"
        loading={knowledgeQ.isLoading}
        error={knowledgeQ.isError ? "Could not load knowledge documents." : null}
      >
        <div className="space-y-3 text-sm">
          <KnowledgeGroup
            label="Matching rules (from Knowledge)"
            docs={docsByGroup.matching}
            categories={["property_matching_rules"]}
            empty="No matching rules yet."
            emptyHint="How the AI proposes alternatives when an exact match is missing (budget tolerance, area swaps, bedroom flexibility)."
            exampleTitle="Matching rules - budget ±15% and adjacent areas"
          />
          <DefaultsNotice />
        </div>
      </Section>

      {/* 7. Policies & Processes */}
      <Section
        icon={<ScrollText className="h-4 w-4" />}
        title="Policies & Processes"
        subtitle="Payment, viewing, contract"
        editHref="/knowledge"
        editLabel="Edit Knowledge"
        loading={knowledgeQ.isLoading}
        error={knowledgeQ.isError ? "Could not load knowledge documents." : null}
      >
        <div className="space-y-3 text-sm">
          <KnowledgeGroup
            label="Policy docs"
            docs={docsByGroup.policies}
            categories={["payment_terms", "viewing_rules", "contract_rules"]}
            empty="No policy docs yet."
            emptyHint="Payment terms, viewing rules, and contract rules the AI must explain consistently across every conversation."
            exampleTitle="Payment terms - 50% upfront, balance on key handover"
          />
          <DefaultsNotice />
        </div>
      </Section>

      {/* 8. Sales Scripts */}
      <Section
        icon={<MessageSquare className="h-4 w-4" />}
        title="Sales Scripts"
        subtitle="Greetings + qualification flow"
        editHref="/knowledge"
        editLabel="Edit Knowledge"
        loading={knowledgeQ.isLoading}
        error={knowledgeQ.isError ? "Could not load knowledge documents." : null}
      >
        <div className="space-y-3 text-sm">
          <KnowledgeGroup
            label="Sales + qualification scripts"
            docs={docsByGroup.sales}
            categories={["sales_scripts", "qualification_scripts"]}
            empty="No sales scripts yet."
            emptyHint="Greeting templates, discovery questions, and qualification flows the AI uses to triage new prospects."
            exampleTitle="Qualification - confirm budget, area, dates, group size"
          />
        </div>
      </Section>

      {/* 9. Objections */}
      <Section
        icon={<Shield className="h-4 w-4" />}
        title="Objections"
        subtitle="How the AI handles resistance"
        editHref="/knowledge"
        editLabel="Edit Knowledge"
        loading={knowledgeQ.isLoading}
        error={knowledgeQ.isError ? "Could not load knowledge documents." : null}
      >
        <div className="space-y-3 text-sm">
          <KnowledgeGroup
            label="Objection-handling docs"
            docs={docsByGroup.objections}
            categories={["objection_handling"]}
            empty="No objection docs yet."
            emptyHint="Pre-written responses to common pushbacks - 'too expensive', 'I'll think about it', 'send me other options'."
            exampleTitle="Objection - 'too expensive': reframe by stay duration"
          />
        </div>
      </Section>

      {/* 9b. FAQ & Common Questions */}
      <Section
        icon={<HelpCircle className="h-4 w-4" />}
        title="FAQ & Common Questions"
        subtitle="Recurring questions the AI must answer the same way"
        editHref="/knowledge"
        editLabel="Edit Knowledge"
        loading={knowledgeQ.isLoading}
        error={knowledgeQ.isError ? "Could not load knowledge documents." : null}
      >
        <div className="space-y-3 text-sm">
          <KnowledgeGroup
            label="FAQ docs"
            docs={docsByGroup.faq}
            categories={["faq"]}
            empty="No FAQ docs yet."
            emptyHint="Frequent prospect questions the AI must answer the same way every time - payment, deposit, taxes, pets, internet speed, etc."
            exampleTitle="FAQ - Do you accept crypto? What's the deposit?"
          />
        </div>
      </Section>

      {/* 10. Area Knowledge */}
      <Section
        icon={<MapPin className="h-4 w-4" />}
        title="Area Knowledge"
        subtitle="Where you operate + area-specific docs"
        editHref="/business"
        editLabel="Edit Business"
        loading={bizQ.isLoading || knowledgeQ.isLoading}
        error={bizQ.isError ? "Could not load business settings." : null}
      >
        <div className="space-y-3 text-sm">
          {biz && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Coverage</div>
              <KV label="Cities covered" value={citiesString(biz.cities_covered)} />
              <KV label="Cities NOT covered" value={biz.areas_not_covered} multiline />
            </div>
          )}
          <KnowledgeGroup
            label="Area knowledge docs"
            docs={docsByGroup.area}
            categories={["area_knowledge"]}
            empty="No area docs yet."
            emptyHint="Neighbourhood briefings - vibe, commute, beach access, cafes, schools - so the AI can answer 'what's it like to live there?'"
            exampleTitle="Area - Canggu vs Pererenan: surf, traffic, expats"
          />
        </div>
      </Section>

      {/* 11. Auto Sources */}
      <Section
        icon={<Globe className="h-4 w-4" />}
        title="Auto Sources"
        subtitle="Scraped websites"
        editHref="/knowledge"
        editLabel="Manage in Knowledge"
        loading={sourcesQ.isLoading}
        error={sourcesQ.isError ? "Could not load auto sources." : null}
      >
        {sources.length === 0 ? (
          <p className="text-sm text-slate-500">
            No auto sources yet. Paste a URL in <Link to="/knowledge" className="underline">Knowledge → Auto sources</Link> to scrape and index a website.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sources.map((s) => (
              <li key={s.id} className="py-2 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <a href={s.url} target="_blank" rel="noreferrer" className="font-medium text-slate-900 hover:underline truncate">
                    {s.label || s.url}
                  </a>
                  <SourceStatus status={s.status} />
                </div>
                <div className="text-[11px] text-slate-500 truncate">{s.url}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {s.doc_count} sections{s.last_scraped_at && ` · last scrape ${formatAgo(s.last_scraped_at)}`}
                </div>
                {s.last_error && (
                  <div className="text-[11px] text-red-700 bg-red-50 mt-1 px-2 py-1 rounded">
                    {s.last_error}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 11b. Knowledge Coverage */}
      <Section
        icon={<BarChart3 className="h-4 w-4" />}
        title="Knowledge Coverage"
        subtitle={`${coverage.activeDocs} active docs · ${coverage.missingCategories.length} missing categories`}
        editHref="/knowledge"
        editLabel="Edit Knowledge"
        loading={knowledgeQ.isLoading || sourcesQ.isLoading}
        error={knowledgeQ.isError ? "Could not load knowledge documents." : null}
      >
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Active docs" value={coverage.activeDocs} accent={coverage.activeDocs >= 5} />
            <Stat label="Auto sources" value={sources.length} />
            <Stat
              label="Missing categories"
              value={coverage.missingCategories.length}
              muted={coverage.missingCategories.length === 0}
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Per category</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {coverage.perCategory.map((c) => (
                <li key={c.category} className="flex items-center justify-between text-sm py-0.5">
                  <span className="text-slate-700">{c.category}</span>
                  {c.count > 0 ? (
                    <Badge variant="muted">{c.count}</Badge>
                  ) : (
                    <Link
                      to={`/knowledge?category=${c.category}`}
                      className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900 hover:underline"
                    >
                      <Badge variant="danger">Missing</Badge>
                      Add
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            Embedding model: <code className="text-[10px] bg-slate-100 px-1 rounded">{biz?.embedding_model || "openai/text-embedding-3-small (default)"}</code>
          </p>
        </div>
      </Section>

      {/* 12. RAG & Indexing */}
      <Section
        icon={<Sparkles className="h-4 w-4" />}
        title="RAG & Indexing"
        subtitle="Vector retrieval status"
        editHref="/ai-bot"
        editLabel="Edit AI Bot"
        loading={bizQ.isLoading || knowledgeQ.isLoading || sourcesQ.isLoading}
      >
        <div className="space-y-2 text-sm">
          <KV label="Active knowledge docs" value={String(knowledge.filter((k) => k.is_active === 1).length)} />
          <KV label="Auto sources" value={String(sources.length)} />
          <KV label="Embedding model" value={biz?.embedding_model || "openai/text-embedding-3-small (default)"} />
          <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            Knowledge documents are split into chunks and embedded as 1536-dim vectors. On every inbound message, the system retrieves the top-5 closest chunks (cosine similarity) and injects them in the <code className="text-[10px] bg-slate-100 px-1 rounded">KNOWLEDGE_SNIPPETS</code> block of the AI prompt.
            If embedding fails, falls back to keyword search.
          </p>
          <p className="text-[11px] text-slate-400 italic">
            Per-doc indexing status (last_indexed_at) is not exposed in the list endpoint yet.
          </p>
        </div>
      </Section>

      {/* 13. Property Data Health */}
      <Section
        icon={<Activity className="h-4 w-4" />}
        title="Property Data Health"
        subtitle="Inventory snapshot + per-villa completeness"
        editHref="/properties"
        editLabel="Edit Properties"
        loading={portfolioQ.isLoading || propsQ.isLoading}
        error={portfolioQ.isError ? "Could not load portfolio summary." : null}
      >
        {portfolio && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Active" value={portfolio.counts.active} />
              <Stat label="Available now" value={portfolio.counts.available_now} accent />
              <Stat label="All-time" value={portfolio.counts.total} muted />
            </div>
            {portfolio.areas.length > 0 && (
              <div className="text-xs text-slate-600">
                <span className="text-slate-500">Areas with active properties: </span>
                {portfolio.areas.join(", ")}
              </div>
            )}

            <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
              Properties are the <strong>source of truth</strong> for price, availability, bedrooms, area, and property-specific rules (pets, smoking, deposit, etc.). The AI reads these per-villa fields whenever a property is mentioned - they override the global rules from Knowledge.
            </p>

            {/* Property-specific rules health */}
            <div className="pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Per-villa rules health
              </div>
              {propsHealth.active === 0 ? (
                <p className="text-xs text-slate-500">
                  No active properties yet - add one in <Link to="/properties" className="underline">Properties</Link>.
                </p>
              ) : (
                <>
                  <ul className="space-y-1 text-sm">
                    <PropFieldRow label="Monthly price"      missing={propsHealth.missingMonthlyPrice}     total={propsHealth.active} />
                    <PropFieldRow label="Deposit"            missing={propsHealth.missingDeposit}          total={propsHealth.active} />
                    <PropFieldRow label="Minimum stay"       missing={propsHealth.missingMinStay}          total={propsHealth.active} />
                    <PropFieldRow label="Short description"  missing={propsHealth.missingShortDescription} total={propsHealth.active} />
                  </ul>
                  <p className="text-[11px] text-slate-500 italic mt-2 flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-500" />
                    <span>
                      Detailed per-villa rule completeness (pets policy, smoking policy, wifi, viewing rules, house rules) requires opening each villa in{" "}
                      <Link to="/properties" className="underline">/properties</Link>.
                      The list API does not return those fields here.
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

// ===========================================================================
// Generic Section component
// ===========================================================================

function Section({
  icon, title, subtitle, editHref, editLabel, defaultOpen, loading, error, children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  editHref?: string;
  editLabel?: string;
  defaultOpen?: boolean;
  loading?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <details open={defaultOpen} className="group">
        <summary className="cursor-pointer list-none px-5 py-3 flex items-center gap-2 select-none hover:bg-slate-50 transition-colors rounded-t-lg">
          <span className="text-slate-700">{icon}</span>
          <CardTitle className="text-sm flex-1">{title}</CardTitle>
          {subtitle && (
            <span className="text-[11px] text-slate-500 truncate">{subtitle}</span>
          )}
          <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
        </summary>
        <CardContent className="border-t border-slate-200 pt-4">
          {loading ? (
            <div className="text-xs text-slate-400">Loading…</div>
          ) : error ? (
            <NotAvailable label={error} />
          ) : (
            children
          )}
          {editHref && !loading && !error && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
              <Link
                to={editHref}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 hover:underline"
              >
                {editLabel ?? `Edit in ${editHref}`}
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          )}
        </CardContent>
      </details>
    </Card>
  );
}

// ===========================================================================
// Small atoms
// ===========================================================================

function KV({ label, value, multiline }: { label: string; value: unknown; multiline?: boolean }) {
  const raw = value === undefined || value === null || value === "" ? null : String(value);
  return (
    <div className={cn("flex gap-3", multiline ? "items-start flex-col sm:flex-row" : "items-baseline")}>
      <span className="text-xs text-slate-500 shrink-0 sm:w-40">{label}</span>
      {raw ? (
        <span className={cn(
          "text-sm text-slate-900",
          multiline ? "whitespace-pre-wrap break-words" : "truncate",
        )}>
          {raw}
        </span>
      ) : (
        <span className="text-xs text-slate-400 italic">- not set -</span>
      )}
    </div>
  );
}

function NotAvailable({ label }: { label: string }) {
  return (
    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
      Not available yet - {label}
    </div>
  );
}

function DefaultsNotice() {
  return (
    <p className="text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5">
      💡 These are <strong>default rules</strong>. Individual villas can override them in{" "}
      <Link to="/properties" className="underline">Properties → Villa → House rules</Link>.
    </p>
  );
}

function Stat({ label, value, accent, muted }: { label: string; value: number; accent?: boolean; muted?: boolean }) {
  return (
    <div className={cn(
      "rounded-md border p-2 text-center",
      accent ? "bg-green-50 border-green-200" :
      muted  ? "bg-slate-50 border-slate-200" :
               "bg-brand-surface border-slate-200",
    )}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={cn(
        "text-xl font-semibold tabular-nums",
        accent ? "text-green-700" : "text-slate-900",
      )}>{value}</div>
    </div>
  );
}

function PropFieldRow({ label, missing, total }: { label: string; missing: number; total: number }) {
  const filled = total - missing;
  const ok = missing === 0;
  return (
    <li className="flex items-center justify-between gap-2 text-sm">
      <span className="text-slate-700 flex items-center gap-1.5">
        {ok ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Circle className="h-3.5 w-3.5 text-slate-300" />
        )}
        {label}
      </span>
      <span className="flex items-center gap-2">
        <span className={cn("text-xs tabular-nums", ok ? "text-slate-600" : "text-slate-500")}>
          {filled}/{total}
        </span>
        {!ok && <Badge variant="warning">{missing} missing</Badge>}
      </span>
    </li>
  );
}

function SourceStatus({ status }: { status: string }) {
  const tone =
    status === "success" ? "success" :
    status === "error"   ? "danger"  :
                            "muted";
  return <Badge variant={tone as "success" | "danger" | "muted"}>{status}</Badge>;
}

function KnowledgeGroup({
  label, docs, categories, empty, emptyHint, exampleTitle,
}: {
  label: string;
  docs: KnowledgeDoc[];
  categories: string[];
  /** Short legacy line - kept for BB1 callers, used as the heading of the empty card. */
  empty: string;
  /** What this section is for, in one sentence - surfaced on empty state. */
  emptyHint?: string;
  /** One example doc title that would belong here. */
  exampleTitle?: string;
}) {
  void categories; // accepted for future filtering / debug
  if (docs.length === 0) {
    return (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
        <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 space-y-1">
          <p className="text-xs text-slate-700">{empty}</p>
          {emptyHint && <p className="text-[11px] text-slate-500">{emptyHint}</p>}
          {exampleTitle && (
            <p className="text-[11px] text-slate-400 italic">
              Example title: <span className="not-italic">"{exampleTitle}"</span>
            </p>
          )}
          <Link
            to="/knowledge"
            className="text-[11px] text-slate-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
          >
            Add a doc in Knowledge
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {label} <span className="text-slate-400">({docs.length})</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.id} className="py-1.5">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium text-slate-900 truncate flex-1">{d.title}</span>
              <Badge variant="muted">{d.category}</Badge>
              <span className="text-[10px] text-slate-400">P{d.priority}</span>
            </div>
            {d.body_preview && (
              <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{d.body_preview}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function groupKnowledge(docs: KnowledgeDoc[]) {
  const groups: Record<string, KnowledgeDoc[]> = {
    policies: [], sales: [], objections: [], area: [], matching: [], offer: [], faq: [],
  };
  for (const d of docs) {
    if (d.is_active !== 1) continue;
    const g = KB_GROUP[d.category];
    if (g && groups[g]) groups[g]!.push(d);
  }
  // Sort each group by priority desc
  for (const k of Object.keys(groups)) {
    groups[k]!.sort((a, b) => b.priority - a.priority);
  }
  return groups;
}

function citiesString(raw: unknown): string | null {
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.join(", ");
    } catch { /* fall through */ }
    return raw;
  }
  return null;
}

function fmtNum(n: number | string | null | undefined): string | null {
  if (n === null || n === undefined) return null;
  const x = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(x)) return null;
  return String(x);
}

function formatAgo(iso: string): string {
  try {
    const d = parseBackendDate(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1)   return "just now";
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    const day = Math.floor(h / 24);
    return `${day}d ago`;
  } catch {
    return iso;
  }
}

// ===========================================================================
// Setup checklist
// ===========================================================================

interface ChecklistItem {
  label: string;
  done: boolean;
  hint?: string;
  /** When set, the Knowledge category that this item maps to - used to show
   *  a "Missing" badge + a deep link to /knowledge?category=… on the row. */
  category?: string;
}

function computeChecklist({
  biz, portfolio, knowledge, secrets,
}: {
  biz: BusinessSettings | undefined;
  portfolio: PortfolioSummary | undefined;
  knowledge: KnowledgeDoc[];
  secrets: SecretsStatus | undefined;
}): { items: ChecklistItem[]; done: number; total: number } {
  const activeDocs = knowledge.filter((k) => k.is_active === 1);
  const hasDoc = (cat: string) => activeDocs.some((d) => d.category === cat);

  const items: ChecklistItem[] = [
    {
      label: "Business name set",
      done: !!biz?.business_name && biz.business_name !== "AI Leasing OS - Default",
    },
    {
      label: "Business description / offering filled",
      done: !!(biz?.description?.trim() || biz?.offering_description?.trim()),
    },
    {
      label: "ICP filled",
      done: !!biz?.icp_description?.trim() && biz.icp_description.trim().length >= 30,
    },
    {
      label: "At least 1 active property",
      done: (portfolio?.counts.active ?? 0) >= 1,
    },
    {
      label: "At least 3 active knowledge docs",
      done: activeDocs.length >= 3,
      hint: `${activeDocs.length} so far`,
    },
    {
      label: "Payment terms doc exists",
      done: hasDoc("payment_terms"),
    },
    {
      label: "Viewing rules doc exists",
      done: hasDoc("viewing_rules"),
    },
    {
      label: "Property matching rules doc exists",
      done: hasDoc("property_matching_rules"),
    },
  ];

  // Per-category missing-knowledge checks. Each shows a "Missing" badge + a
  // dedicated "Add in Knowledge" deep link when not satisfied (rendered by
  // the checklist UI below).
  const CRITICAL_CATEGORIES: { category: string; label: string }[] = [
    { category: "payment_terms",           label: "Payment terms doc"           },
    { category: "viewing_rules",           label: "Viewing rules doc"           },
    { category: "property_matching_rules", label: "Property matching rules doc" },
    { category: "objection_handling",      label: "Objection-handling doc"      },
    { category: "area_knowledge",          label: "Area knowledge doc"          },
    { category: "faq",                     label: "FAQ doc"                     },
    { category: "contract_rules",          label: "Contract rules doc"          },
  ];
  // Replace the legacy hand-coded payment_terms / viewing_rules / matching
  // entries (added above) with this single uniform per-category sweep so
  // every missing doc gets the same "Missing" + "Add in Knowledge" UX.
  const filtered = items.filter(
    (it) =>
      it.label !== "Payment terms doc exists" &&
      it.label !== "Viewing rules doc exists" &&
      it.label !== "Property matching rules doc exists",
  );
  for (const { category, label } of CRITICAL_CATEGORIES) {
    filtered.push({
      label,
      done: hasDoc(category),
      category,
    });
  }

  if (secrets) {
    filtered.push({
      label: "WhatsApp connected (WAHA secrets set)",
      done: secrets.waha_api_key && secrets.waha_base_url && secrets.waha_default_session,
    });
    filtered.push({
      label: "Embedding/RAG ready (OpenRouter key set)",
      done: secrets.openrouter_api_key,
    });
  }

  return {
    items: filtered,
    done: filtered.filter((i) => i.done).length,
    total: filtered.length,
  };
}

// ===========================================================================
// Knowledge coverage + properties health helpers
// ===========================================================================

const ALL_KB_CATEGORIES: string[] = [
  "company_rules",
  "payment_terms",
  "viewing_rules",
  "contract_rules",
  "sales_scripts",
  "qualification_scripts",
  "objection_handling",
  "area_knowledge",
  "property_matching_rules",
  "faq",
];

interface CoverageReport {
  perCategory: { category: string; count: number }[];
  activeDocs: number;
  missingCategories: string[];
}

function computeCoverage(knowledge: KnowledgeDoc[]): CoverageReport {
  const active = knowledge.filter((k) => k.is_active === 1);
  const perCategory = ALL_KB_CATEGORIES.map((category) => ({
    category,
    count: active.filter((k) => k.category === category).length,
  }));
  return {
    perCategory,
    activeDocs: active.length,
    missingCategories: perCategory.filter((c) => c.count === 0).map((c) => c.category),
  };
}

interface PropertiesHealthReport {
  total: number;
  active: number;
  missingMinStay: number;
  missingDeposit: number;
  missingMonthlyPrice: number;
  missingShortDescription: number;
}

function computePropertiesHealth(properties: PropertyListItem[]): PropertiesHealthReport {
  const active = properties.filter((p) => p.is_active === 1);
  return {
    total: properties.length,
    active: active.length,
    missingMinStay:          active.filter((p) => p.min_stay_months == null).length,
    missingDeposit:          active.filter((p) => p.deposit_idr == null || p.deposit_idr <= 0).length,
    missingMonthlyPrice:     active.filter((p) => p.monthly_price_idr == null || p.monthly_price_idr <= 0).length,
    missingShortDescription: active.filter((p) => !p.short_description?.trim()).length,
  };
}
